
import {
	HttpEvent,
	HttpHandlerFn,
	HttpRequest
} from '@angular/common/http';
import { DOCUMENT, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { defer, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { AUTH_JWT_OPTIONS } from '../tokens/auth-jwt-options.token';
import { applyAuthJwtConfigDefaults, AuthJwtConfig } from './auth-jwt-config.model';
import { AuthJwtHelperService } from '../services/auth-jwt-helper.service';
import { isPlatformBrowser } from '@angular/common';


const fromPromiseOrValue = <T>(input: T | Promise<T>) => {
	if (input instanceof Promise) {
		return defer(() => input);
	}
	return of(input);
};


@Injectable({providedIn: 'root'})
export class AuthJwtInterceptor {
	private document = inject(DOCUMENT);
	private config = applyAuthJwtConfigDefaults(inject<AuthJwtConfig>(AUTH_JWT_OPTIONS));
	private jwtHelper = inject(AuthJwtHelperService);

	constructor() {
		// console.log('AuthJwtInterceptor created with config', this.config);
	}



	isAllowedDomain(request: HttpRequest<unknown>): boolean {
		const requestUrl: URL = new URL(request.url, this.document?.location?.origin);

		// If the host equals the current window origin,
		// the domain is allowed by default
		if (requestUrl.host === this.document.location.host) {
			return true;
		}

		// If not the current domain, check the allowed list
		const hostName = `${requestUrl.hostname}${requestUrl.port && !this.config.standardPorts.includes(requestUrl.port)
			? ':' + requestUrl.port
			: ''
			}`;

		return (
			this.config.allowedDomains.findIndex((domain) =>
				typeof domain === 'string'
					? domain === hostName
					: domain instanceof RegExp
						? domain.test(hostName)
						: false
			) > -1
		);
	}

	isDisallowedRoute(request: HttpRequest<unknown>): boolean {

		const requestedUrl: URL = new URL(
			request.url,
			this.document.location.origin
		);

		return (
			this.config.disallowedRoutes.findIndex((route: string | RegExp) => {
				if (typeof route === 'string') {
					const parsedRoute: URL = new URL(
						route,
						this.document.location.origin
					);
					return (
						parsedRoute.hostname === requestedUrl.hostname &&
						parsedRoute.pathname === requestedUrl.pathname
					);
				}

				if (route instanceof RegExp) {
					return route.test(request.url);
				}

				return false;
			}) > -1
		);
	}


	handleInterception(
		token: string | null,
		request: HttpRequest<unknown>,
		next: HttpHandlerFn
	) {
		//console.log ('handleInterception')
		const authScheme = this.jwtHelper.getAuthScheme(this.config.authScheme, request);
		//console.log ('handleInterception A')

		if (!token && this.config.throwNoTokenError) {
			//console.log ('no token');
			throw new Error('Could not get token from tokenGetter function.');
		}

		let tokenIsExpired: Observable<boolean> = of(false);

		if (this.config.skipWhenExpired) {
			tokenIsExpired = token ? fromPromiseOrValue(this.jwtHelper.isTokenExpired(token)) : of(true);
		}
		//console.log ('handleInterception B')
		if (token) {
			//console.log ('handleInterception C, token: ', token)
			//this.logService.log('token', token);
			return tokenIsExpired.pipe(
				map((isExpired) =>
					isExpired && this.config.skipWhenExpired
						? request.clone()
						: request.clone({
							setHeaders: {
								[this.config.headerName]: `${authScheme}${token}`,
							},
						})
				),
				mergeMap((innerRequest) => next(innerRequest))
			);
		}
		//this.logService.log('no token');
		//console.log ('no token')
		return next(request);
	}
}


export const authJwtInterceptor = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
	// console.log('authJwtInterceptor called for ', request.url);
	const interceptor = inject(AuthJwtInterceptor);
	const config = applyAuthJwtConfigDefaults(inject<AuthJwtConfig>(AUTH_JWT_OPTIONS));
	if (!interceptor.isAllowedDomain(request) || interceptor.isDisallowedRoute(request) || !isPlatformBrowser(inject(PLATFORM_ID))) {
		// this.logService.log ('no allowed domain or disallowed route: ', request.url);
		return next(request);
	}
	// this.logService.log('allowed domain and allowed route: ', request.url);
	// console.log('authJwtInterceptor config.tokenGetter ', config.tokenGetter);
	const token = config.tokenGetter(request);
	//console.log('authJwtInterceptor token ', token);
	return fromPromiseOrValue(token).pipe(
		mergeMap((asyncToken: string | null) => {
			// console.log('authJwtInterceptor: asyncToken', asyncToken);
			return interceptor.handleInterception(asyncToken, request, next);
		})
	);
}
